import React, { useState, useRef, useEffect, FC, useMemo } from 'react';
import { connect } from 'react-redux';
import { NeuButtonSelector, NeuIcon } from '@neutron/react';
import { EmpDepartment, IDelegateOption } from '../../../../config/interfaces';
import { useGetEmployeeConfigQuery } from '../../../../redux/apis/Users.api';
import { RootState } from '../../../../redux/store';
import {
  ButtonListItem,
  DropdownMenu,
  DropdownToggle,
  Item,
  ItemList,
  SearchInput,
  SelectAllOption,
  TabbedDropdownContainer,
  TabContent,
  TabsHeader
} from './TabbedDropdown.styles';
import { useUserRolesRef } from '../../../../services/UserRoles';

export interface Tab {
  id: string;
  label: string;
  content: string[];
}

interface TabDropdownProps {
  userId: string;
  facilityId: string;
  selectedDelegatedEmployee: IDelegateOption;
  empDepartmentList: EmpDepartment[];
  selectedDepts: EmpDepartment[];
  handleClickDelegate: (delegate: IDelegateOption) => void;
  setSelectedDepts: (depts: EmpDepartment[]) => void;
}

const TabDropdown: FC<TabDropdownProps> = ({
  userId,
  facilityId,
  selectedDelegatedEmployee,
  empDepartmentList,
  selectedDepts,
  handleClickDelegate,
  setSelectedDepts
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(
    selectedDelegatedEmployee.employeeName === 'Facility List'
      ? 'Facility'
      : 'List'
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [delegateListOptions, setDelegateListOptions] = useState<
    IDelegateOption[]
  >([{ employeeName: 'Direct Reports List', hcaid: userId }]);
  const [dropdownSelection, setDropdownSelection] = useState<EmpDepartment[]>(
    []
  );

  const { isIlobRounder } = useUserRolesRef();

  const {
    data: { delegatedToMeEmployees: delegatedToMeList } = {}
    // isError: isGetEmployeeConfigError,
    // isFetching: isGetEmployeeConfigFetching,
    // isLoading: isGetEmployeeConfigLoading
  } = useGetEmployeeConfigQuery(userId ?? '', {
    refetchOnMountOrArgChange: 30
  });

  useEffect(() => {
    const delegateList = [
      ...(delegatedToMeList || []),
      { employeeName: 'Direct Reports List', hcaid: userId }
    ];
    const sortedDelegateList = delegateList.sort((a, b) =>
      a.employeeName.localeCompare(b.employeeName)
    );
    setDelegateListOptions(sortedDelegateList);
  }, [delegatedToMeList]);

  useEffect(() => {
    setDropdownSelection([...empDepartmentList]);
  }, [empDepartmentList]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filterDepartments = useMemo(() => {
    if (searchQuery) {
      return dropdownSelection.filter(
        dept =>
          dept.departmentName
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          dept.departmentId.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return dropdownSelection;
  }, [searchQuery, dropdownSelection]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  const getButtonText = () => {
    if (activeTab === 'Facility') {
      if (
        selectedDepts.length === 0 ||
        selectedDepts.length === empDepartmentList.length
      ) {
        return 'All Departments';
      }
      return `${selectedDepts.length} Department${
        selectedDepts.length > 1 ? 's' : ''
      }`;
    }
    return selectedDelegatedEmployee.employeeName || `Select a list`;
  };

  const isItemSelected = (item: any) => {
    if (activeTab === 'Facility') {
      return selectedDepts.includes(item.departmentId);
    }
    return selectedDelegatedEmployee.hcaid === item.hcaid;
  };

  return (
    <TabbedDropdownContainer
      className="tabbed-dropdown-container"
      ref={dropdownRef}
      data-testid="tabbed-dropdown"
    >
      <DropdownToggle
        type="button"
        onClick={toggleDropdown}
        className="dropdown-toggle"
        data-testid="dropdown-toggle"
      >
        {getButtonText()}
      </DropdownToggle>

      {isOpen && (
        <DropdownMenu className="dropdown-menu" data-testid="dropdown-menu">
          <TabsHeader className="tabs-header">
            {!isIlobRounder && (
              <>
                {' '}
                <NeuButtonSelector
                  data-testid="tabbed-dropdown-button-selector"
                  style={{
                    width: '100%'
                  }}
                  color="blue"
                  kind="single"
                >
                  <NeuButtonSelector
                    data-testid="tabbed-dropdown-button-list-selector-tab"
                    unit="button"
                    active={activeTab === 'List'}
                    style={{
                      border: activeTab === 'List' ? '' : '1px solid #b0b0b0'
                    }}
                    onClick={() => {
                      handleClickDelegate({
                        employeeName: 'Direct Reports List',
                        hcaid: userId
                      });
                      setActiveTab('List');
                    }}
                    onNeuChange={() => {
                      handleClickDelegate({
                        employeeName: 'Direct Reports List',
                        hcaid: userId
                      });
                      setActiveTab('List');
                    }}
                  >
                    Direct/Delegate
                  </NeuButtonSelector>
                  <NeuButtonSelector
                    data-testid="tabbed-dropdown-button-facility-selector-tab"
                    unit="button"
                    active={activeTab === 'Facility'}
                    style={{
                      border:
                        activeTab === 'Facility' ? '' : '1px solid #b0b0b0'
                    }}
                    onClick={() => {
                      handleClickDelegate({
                        employeeName: 'Facility List',
                        hcaid: facilityId
                      });
                      setActiveTab('Facility');
                    }}
                    onNeuChange={() => {
                      handleClickDelegate({
                        employeeName: 'Facility List',
                        hcaid: facilityId
                      });
                      setActiveTab('Facility');
                    }}
                  >
                    Facility
                  </NeuButtonSelector>
                </NeuButtonSelector>
              </>
            )}
          </TabsHeader>
          <TabContent className="tab-content">
            <ItemList className="item-list">
              {activeTab === 'Facility' && (
                <>
                  <SearchInput
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="search-input"
                    data-testid="search-input"
                  />
                  <SelectAllOption
                    role="tab"
                    onClick={() =>
                      setSelectedDepts(
                        selectedDepts.length === empDepartmentList.length
                          ? []
                          : [...empDepartmentList]
                      )
                    }
                    onKeyDown={() =>
                      setSelectedDepts(
                        selectedDepts.length === empDepartmentList.length
                          ? []
                          : [...empDepartmentList]
                      )
                    }
                    className="select-all-option"
                    data-testid="select-all"
                  >
                    <ButtonListItem type="button">Select All</ButtonListItem>
                  </SelectAllOption>
                  {filterDepartments.map(item => (
                    <Item
                      key={item.departmentId}
                      role="tab"
                      value={item.departmentId}
                      onClick={() =>
                        setSelectedDepts(
                          selectedDepts.findIndex(
                            (d: EmpDepartment) =>
                              item.departmentId === d.departmentId
                          ) === -1
                            ? [...selectedDepts, item]
                            : selectedDepts.filter(
                                (d: EmpDepartment) =>
                                  item.departmentId !== d.departmentId
                              )
                        )
                      }
                      onKeyDown={() =>
                        setSelectedDepts(
                          selectedDepts.findIndex(
                            (d: EmpDepartment) =>
                              item.departmentId === d.departmentId
                          ) === -1
                            ? [...selectedDepts, item]
                            : selectedDepts.filter(
                                (d: EmpDepartment) =>
                                  item.departmentId !== d.departmentId
                              )
                        )
                      }
                      className={
                        isItemSelected(item.departmentId) ? 'selected' : ''
                      }
                      data-testid={`department-dropdown-item-${item.departmentId}`}
                    >
                      <ButtonListItem type="button">
                        {item.departmentName} - {item.departmentId}
                      </ButtonListItem>
                      {selectedDepts.some(
                        d => d.departmentId === item.departmentId
                      ) && (
                        <NeuIcon className="custom-checkmark" color="blue">
                          check
                        </NeuIcon>
                      )}
                    </Item>
                  ))}
                </>
              )}
              {activeTab === 'List' &&
                delegateListOptions.map(item => (
                  <Item
                    key={item.hcaid}
                    role="tab"
                    value={item.hcaid}
                    onClick={() => handleClickDelegate(item)}
                    onChange={() => handleClickDelegate(item)}
                    className={isItemSelected(item.hcaid) ? 'selected' : ''}
                    data-testid={`delegate-dropdown-item-${item.hcaid}`}
                  >
                    <ButtonListItem type="button">
                      {item.employeeName}
                    </ButtonListItem>
                  </Item>
                ))}
            </ItemList>
          </TabContent>
        </DropdownMenu>
      )}
    </TabbedDropdownContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  facilityId: state.AuthorizedUser.authorizedUser.facilityId,
  userId: state.AuthorizedUser.authorizedUser.hcaid,
  selectedDelegatedEmployee: state.EmployeeReducer.selectedDelegatedEmployee,
  empDepartmentList: state.ConfigReducer.empDepartments
});
export default connect(mapReduxStateToProps)(TabDropdown);
